import { HttpClient, HttpContext, HttpResponse } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { catchError, map, Observable } from "rxjs";

import { SKIP_ERROR_HANDLER } from "@core/interceptors/http-error.interceptor";
import { ApiUrlBuilderService } from "@core/services/common/api-url-builder.service";
import {
  ArchivoReporte,
  ReporteArchivoService,
} from "@core/services/common/reporte-archivo.service";
import {
  EtiquetaCampo,
  FormatoArchivo,
  ObjetoDatos,
  PedidoEjecucion,
  PlantillaDetalle,
  PlantillaGuardar,
  PlantillaResumen,
  RespuestaLista,
  RespuestaRegistro,
} from "@pages/configuracion/components/plantillas-reportes/interfaces/plantilla-reporte.interface";

const OBJETOS_DE_DATOS: string = "desa_tpcdr_sentencias";

/**
 * Herramienta interna de plantillas de Core: `reports/org/{org}/plantillas-core`. Todos los pedidos
 * saltean el interceptor global: la pantalla muestra cada error donde corresponde.
 */
@Injectable({ providedIn: "root" })
export class PlantillasCoreService {
  private readonly http = inject(HttpClient);
  private readonly api = inject(ApiUrlBuilderService);
  private readonly reporteArchivo = inject(ReporteArchivoService);

  /** Un usuario fuera de la lista recibe 403 y no ve ningún mensaje: el guard lo manda al inicio. */
  public acceso(): Observable<RespuestaRegistro<{ habilitado: boolean }>> {
    return this.http.get<RespuestaRegistro<{ habilitado: boolean }>>(
      this.url("acceso"),
      { context: this.sinErrorGlobal() },
    );
  }

  public getPlantillas(): Observable<PlantillaResumen[]> {
    return this.http
      .get<RespuestaLista<PlantillaResumen>>(this.url(), { context: this.sinErrorGlobal() })
      .pipe(map((r) => r?.lista ?? []));
  }

  public getPlantilla(plantillaId: number): Observable<PlantillaDetalle> {
    return this.http
      .get<RespuestaRegistro<PlantillaDetalle>>(this.url(String(plantillaId)), {
        context: this.sinErrorGlobal(),
      })
      .pipe(map((r) => r.registro));
  }

  public getEtiquetas(): Observable<EtiquetaCampo[]> {
    return this.http
      .get<RespuestaLista<EtiquetaCampo>>(this.url("etiquetas"), { context: this.sinErrorGlobal() })
      .pipe(map((r) => r?.lista ?? []));
  }

  /** Los objetos de datos de la organización del usuario, por el proxy ORDS. */
  public getObjetos(): Observable<ObjetoDatos[]> {
    return this.http
      .get<RespuestaLista<ObjetoDatos>>(this.api.entidadesListarUrl(OBJETOS_DE_DATOS), {
        context: this.sinErrorGlobal(),
      })
      .pipe(
        map((r) =>
          (r?.lista ?? []).map((o) => ({
            objeto: o.objeto,
            referencia: o.referencia ?? null,
            typeofrequest: String(o.typeofrequest ?? "GET").toUpperCase() === "PUT" ? "PUT" : "GET",
          })),
        ),
      );
  }

  public insertar(plantilla: PlantillaGuardar): Observable<PlantillaDetalle> {
    return this.http
      .post<RespuestaRegistro<PlantillaDetalle>>(this.url(), plantilla, {
        context: this.sinErrorGlobal(),
      })
      .pipe(map((r) => r.registro));
  }

  public modificar(plantillaId: number, plantilla: PlantillaGuardar): Observable<PlantillaDetalle> {
    return this.http
      .put<RespuestaRegistro<PlantillaDetalle>>(this.url(String(plantillaId)), plantilla, {
        context: this.sinErrorGlobal(),
      })
      .pipe(map((r) => r.registro));
  }

  /** El back responde 204 sin body. Las columnas se borran con la plantilla. */
  public eliminar(plantillaId: number): Observable<void> {
    return this.http.delete<void>(this.url(String(plantillaId)), {
      context: this.sinErrorGlobal(),
    });
  }

  /**
   * La misma ejecución que usan los formularios. El error llega como `Blob`: lo traduce
   * `ReporteArchivoService.traducirError`, que deja el `errortxt` en un `ErrorReporte`.
   */
  public ejecutar(plantillaId: number, pedido: PedidoEjecucion): Observable<ArchivoReporte> {
    return this.http
      .post(this.url(String(plantillaId), "ejecutar", this.api.usuario), pedido, {
        responseType: "blob",
        observe: "response",
        context: this.sinErrorGlobal(),
      })
      .pipe(
        map((res: HttpResponse<Blob>) => ({
          blob: res.body as Blob,
          filename: this.nombreDelArchivo(res, pedido.formato),
        })),
        catchError((err: unknown) => this.reporteArchivo.traducirError(err)),
      );
  }

  public guardarArchivo(archivo: ArchivoReporte): void {
    this.reporteArchivo.guardar(archivo);
  }

  private nombreDelArchivo(res: HttpResponse<Blob>, formato: FormatoArchivo): string {
    const header: string | null = res.headers?.get("Content-Disposition") ?? null;
    const coincidencia = header ? /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(header) : null;
    return coincidencia?.[1] ? decodeURIComponent(coincidencia[1].trim()) : `reporte.${formato}`;
  }

  private url(...segmentos: string[]): string {
    const org: string = (this.api.organization || "").trim();
    return this.api.buildBaseUrl("reports", "org", org, "plantillas-core", ...segmentos);
  }

  private sinErrorGlobal(): HttpContext {
    return new HttpContext().set(SKIP_ERROR_HANDLER, true);
  }
}
